import React, { useState, useMemo, useRef, useEffect } from 'react';
import { HiChevronLeft, HiChevronRight, HiChevronDown, HiEye, HiEyeOff } from 'react-icons/hi';

// Modern checkbox styles
const checkboxStyles = `
.modern-checkbox {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #d1d5db;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
}

.modern-checkbox:hover {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modern-checkbox:checked {
    background: #3b82f6;
    border-color: #3b82f6;
}

.modern-checkbox:checked::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 2px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.modern-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.modern-checkbox:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
`;

const DataTable = ({ data, onRowClick, selectable = false, selectedRows = [], onSelectionChange = () => {} }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [showAllData, setShowAllData] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({});
    const [showColumnMenu, setShowColumnMenu] = useState(null);
    const columnMenuRef = useRef(null);

    const tableData = useMemo(() => {
        if (!data || Object.keys(data).length === 0) return [];

        // Formato 'split' do pandas: {index: [...], columns: [...], data: [[...]]}
        if (data.Resumo && data.Resumo.dados) {
            const dadosSplit = data.Resumo.dados;

            // Verificar se está no formato 'split'
            if (dadosSplit.columns && dadosSplit.data) {
                const columns = dadosSplit.columns;
                const rows = dadosSplit.data;

                // Converter cada linha em objeto
                return rows.map(row => {
                    const record = {};
                    columns.forEach((colName, idx) => {
                        record[colName] = row[idx];
                    });
                    return record;
                });
            }
        }

        return [];
    }, [data]);

    const columns = useMemo(() => {
        if (!data || !data.Resumo || !data.Resumo.dados || !data.Resumo.dados.columns) {
            return [];
        }

        // Usa a ordem das colunas do formato 'split' (já está ordenada)
        return data.Resumo.dados.columns.map(key => ({
            key,
            label: key.replace(/_/g, ' ').toUpperCase(),
            sortable: true
        }));
    }, [data]);

    // Initialize visible columns when columns change
    useEffect(() => {
        if (columns.length > 0) {
            const initialVisibility = {};
            columns.forEach(col => {
                initialVisibility[col.key] = true; // All columns visible by default
            });
            setVisibleColumns(initialVisibility);
        }
    }, [columns]);

    // Filter visible columns
    const visibleColumnsArray = useMemo(() => {
        return columns.filter(column => visibleColumns[column.key] !== false);
    }, [columns, visibleColumns]);

    // Close column menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
                setShowColumnMenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return tableData;

        return [...tableData].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [tableData, sortConfig]);

    const paginatedData = useMemo(() => {
        if (showAllData) {
            return sortedData;
        }
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, currentPage, itemsPerPage, showAllData]);

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    // Reset to first page when switching between modes
    const handleShowAllToggle = () => {
        setShowAllData(!showAllData);
        setCurrentPage(1);
    };

    // Handle column visibility toggle
    const toggleColumnVisibility = (columnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
        // Don't close menu when toggling individual columns
    };

    // Show/hide all columns
    const toggleAllColumns = (visible) => {
        const newVisibility = {};
        columns.forEach(col => {
            newVisibility[col.key] = visible;
        });
        setVisibleColumns(newVisibility);
        // Keep menu open when using "Show All" / "Hide All" buttons
    };

    // Handle column menu toggle
    const handleColumnMenuToggle = (columnKey, event) => {
        event.stopPropagation();
        setShowColumnMenu(showColumnMenu === columnKey ? null : columnKey);
    };

    const handleSort = (columnKey) => {
        setSortConfig(prevConfig => ({
            key: columnKey,
            direction: prevConfig.key === columnKey && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const formatCellValue = (value, key) => {
        if (value === null || value === undefined) return '-';

        if (typeof value === 'number') {
            if (key.toLowerCase().includes('trl') || key.toLowerCase().includes('crl')) {
                return value.toFixed(0);
            }
            if (value % 1 !== 0) {
                return value.toFixed(2);
            }
            return value.toLocaleString();
        }

        return String(value);
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return '⇅';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    // Selection functions
    const isRowSelected = (row, index) => {
        // Use index as a unique identifier for each row
        const rowId = getRowId(row, index);
        return selectedRows.includes(rowId);
    };

    const getRowId = (row, index) => {
        // Try to use 'index' column if it exists, otherwise use table index
        return row.index !== undefined ? row.index : `row-${index}`;
    };

    const handleRowSelection = (row, index, isSelected) => {
        const rowId = getRowId(row, index);
        let newSelection = [...selectedRows];

        if (isSelected) {
            if (!newSelection.includes(rowId)) {
                newSelection.push(rowId);
            }
        } else {
            newSelection = newSelection.filter(id => id !== rowId);
        }

        onSelectionChange(newSelection);
    };

    const handleSelectAll = () => {
        const allRowIds = paginatedData.map((row, index) => getRowId(row, index));
        const allSelected = allRowIds.every(id => selectedRows.includes(id));

        if (allSelected) {
            // Deselect all visible rows
            const newSelection = selectedRows.filter(id => !allRowIds.includes(id));
            onSelectionChange(newSelection);
        } else {
            // Select all visible rows (merge with existing selection)
            const newSelection = [...new Set([...selectedRows, ...allRowIds])];
            onSelectionChange(newSelection);
        }
    };

    const areAllVisibleSelected = () => {
        if (paginatedData.length === 0) return false;
        const allRowIds = paginatedData.map((row, index) => getRowId(row, index));
        return allRowIds.every(id => selectedRows.includes(id));
    };

    const areSomeVisibleSelected = () => {
        if (paginatedData.length === 0) return false;
        const allRowIds = paginatedData.map((row, index) => getRowId(row, index));
        return allRowIds.some(id => selectedRows.includes(id));
    };

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum dado disponível</p>
            </div>
        );
    }

    if (tableData.length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum registro encontrado com os filtros aplicados</p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col max-w-full">
            {/* Inject modern checkbox styles */}
            <style dangerouslySetInnerHTML={{ __html: checkboxStyles }} />

            {/* Controls and counter */}
            <div className="flex-shrink-0 bg-gray-100 px-4 py-2 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700 font-medium">
                            Total de registros: {sortedData.length.toLocaleString()}
                        </div>
                        {selectable && selectedRows.length > 0 && (
                            <div className="text-sm text-blue-700 font-medium bg-blue-100 px-2 py-1 rounded">
                                {selectedRows.length} projeto(s) selecionado(s)
                            </div>
                        )}
                        {!showAllData && (
                            <div className="text-sm text-gray-600">
                                Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, sortedData.length)} - {Math.min(currentPage * itemsPerPage, sortedData.length)}
                            </div>
                        )}

                        {/* Hidden columns indicator */}
                        {Object.values(visibleColumns).some(visible => !visible) && (
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded border">
                                    {Object.values(visibleColumns).filter(v => !v).length} coluna(s) oculta(s)
                                </span>
                                <button
                                    onClick={() => toggleAllColumns(true)}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    Mostrar todas
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-3">
                        <label className="flex items-center space-x-2 text-sm text-gray-700">
                            <input
                                type="checkbox"
                                checked={showAllData}
                                onChange={handleShowAllToggle}
                                className="modern-checkbox"
                            />
                            <span>Mostrar todos</span>
                        </label>

                        {/* Column Visibility Control */}
                        <div className="relative" ref={columnMenuRef}>
                            <button
                                onClick={() => setShowColumnMenu(showColumnMenu === 'global' ? null : 'global')}
                                className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                            >
                                <HiEye className="w-4 h-4" />
                                <span>Colunas</span>
                                <HiChevronDown className="w-4 h-4" />
                            </button>

                            {showColumnMenu === 'global' && (
                                <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                                    <div className="p-3 border-b border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-900">Colunas</span>
                                            <span className="text-xs text-gray-500">
                                                {Object.values(visibleColumns).filter(Boolean).length} de {columns.length} visíveis
                                            </span>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => toggleAllColumns(true)}
                                                className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 border border-blue-200"
                                            >
                                                Mostrar Todas
                                            </button>
                                            <button
                                                onClick={() => toggleAllColumns(false)}
                                                className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100 border border-gray-200"
                                            >
                                                Ocultar Todas
                                            </button>
                                        </div>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto">
                                        {columns.map((column) => (
                                            <label
                                                key={column.key}
                                                className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns[column.key] !== false}
                                                    onChange={() => toggleColumnVisibility(column.key)}
                                                    className="modern-checkbox"
                                                />
                                                <span className="text-sm text-gray-700 truncate">
                                                    {column.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {!showAllData && (
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                            >
                                <option value={25}>25 por página</option>
                                <option value={50}>50 por página</option>
                                <option value={100}>100 por página</option>
                                <option value={200}>200 por página</option>
                            </select>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Container with constrained dimensions */}
            <div className="flex-1 overflow-hidden">
                <div className="w-full h-full overflow-auto custom-scrollbar">
                    <table className="divide-y divide-gray-200" style={{ width: 'max-content', minWidth: '100%', outline: 'none' }}>
                        <thead className="bg-gray-50 sticky top-0" style={{ zIndex: 10 }}>
                            <tr style={{ outline: 'none' }}>
                                {selectable && (
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '60px', position: 'relative', zIndex: 11 }}>
                                        <div className="flex items-center justify-center">
                                            <input
                                                type="checkbox"
                                                checked={areAllVisibleSelected()}
                                                ref={(el) => {
                                                    if (el) el.indeterminate = areSomeVisibleSelected() && !areAllVisibleSelected();
                                                }}
                                                onChange={handleSelectAll}
                                                className="modern-checkbox"
                                                title="Selecionar todos os projetos visíveis"
                                                style={{ position: 'relative', zIndex: 12 }}
                                            />
                                        </div>
                                    </th>
                                )}
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: '80px', position: 'relative', zIndex: 11 }}>
                                    #
                                </th>
                                {visibleColumnsArray.map((column) => (
                                    <th
                                        key={column.key}
                                        className="group px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                                        style={{ minWidth: '120px', position: 'relative', zIndex: 11 }}
                                        onClick={() => column.sortable && handleSort(column.key)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-1">
                                                <span>{column.label}</span>
                                                {column.sortable && (
                                                    <span className="text-gray-400">{getSortIcon(column.key)}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => handleColumnMenuToggle(column.key, e)}
                                                    className="ml-2 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Opções da coluna"
                                                >
                                                    <HiChevronDown className="w-3 h-3" />
                                                </button>

                                                {showColumnMenu === column.key && (
                                                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                                        <div className="p-2">
                                                            <button
                                                                onClick={() => toggleColumnVisibility(column.key)}
                                                                className="w-full flex items-center space-x-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded"
                                                            >
                                                                <HiEyeOff className="w-4 h-4" />
                                                                <span>Ocultar esta coluna</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" style={{ position: 'relative', zIndex: 1 }}>
                            {paginatedData.map((row, index) => {
                                // Calculate actual row number accounting for pagination
                                const actualRowNumber = showAllData ? index + 1 : (currentPage - 1) * itemsPerPage + index + 1;
                                return (
                                    <tr
                                        key={index}
                                        style={{ outline: 'none', position: 'relative', zIndex: 1 }}
                                        className={`${onRowClick ? "hover:bg-gray-50 cursor-pointer" : ""} ${selectable && isRowSelected(row, index) ? "bg-blue-50" : ""}`}
                                        onClick={(e) => {
                                            // Don't trigger row click when clicking on checkbox
                                            if (e.target.type === 'checkbox') return;

                                            if (onRowClick) {
                                                onRowClick(row, actualRowNumber);
                                            }
                                        }}
                                    >
                                        {selectable && (
                                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap" style={{ minWidth: '60px', position: 'relative', zIndex: 1 }}>
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isRowSelected(row, index)}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleRowSelection(row, index, e.target.checked);
                                                        }}
                                                        className="modern-checkbox"
                                                        title={`Selecionar projeto ${getRowId(row, index)}`}
                                                        style={{ position: 'relative', zIndex: 2 }}
                                                    />
                                                </div>
                                            </td>
                                        )}
                                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap" style={{ minWidth: '80px', position: 'relative', zIndex: 1 }}>
                                            {actualRowNumber}
                                        </td>
                                        {visibleColumnsArray.map((column) => (
                                            <td key={column.key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" style={{ minWidth: '120px', maxWidth: '200px', position: 'relative', zIndex: 1 }}>
                                                <div className="truncate" title={formatCellValue(row[column.key], column.key)}>
                                                    {formatCellValue(row[column.key], column.key)}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Controls */}
            {!showAllData && totalPages > 1 && (
                <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            <HiChevronLeft className="w-4 h-4" />
                        </button>

                        <span className="text-sm text-gray-700">
                            Página {currentPage} de {totalPages}
                        </span>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            <HiChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="text-sm text-gray-500">
                        {totalPages} páginas no total
                    </div>
                </div>
            )}

        </div>
    );
};

export default DataTable;